// Cache locale (IndexedDB) dei DATI PIENI per il pattern manifest+cache — pezzo distinto da
// manifestCache.ts (che conserva solo lo snapshot leggero recordId+modId usato per il diff).
// Qui vive il risultato assemblato del fetch mirato (server/src/services/uscite_service.ts::
// getUsciteTargetedFetch + uscitaAssembler.ts::assembleTargetedFetchResults): una entità per
// store, chiavata per recordId FM — così un aggiornamento parziale (es. un solo volantino
// cambiato) tocca solo il suo record, non l'intera uscita.
import { openDB, IDBPDatabase } from 'idb';
import type { LocalitaFeature, Volantino, Ragazzo, Carico, Rifornimento, Uscita } from './api';
import type { ManifestEntry, ManifestDiffPlan } from './manifestDiff';

// Stessa forma dei campi "header" restituiti da uscitaAssembler.ts::mapUscitaRoot — tutto tranne
// gli array figli (localita/ragazzi/capogruppo/carico/rifornimento/todo), che nel fetch mirato
// arrivano da tabelle separate e vengono ricomposti solo in buildUsciteFromCache.
export interface UscitaRootCache {
  mob_usc_id: number;
  recordId: number;
  mob_soc_id: number;
  mob_usc_data: string;
  mob_usc_num: number;
  mob_mag_id: number;
  mob_mag_nome: string;
  mob_mag_latlon: string;
  mob_usc_auto_targa: string;
  mob_usc_note_x_cg: string;
  mob_usc_1_fornitore: number;
  mob_ck_1_show_orari_prev: number;
  usc_ora_apertura: string;
  usc_ora_chiusura: string;
  usc_ora_inizio_pausa: string;
  usc_ora_fine_pausa: string;
  usc_pausa_tot_ore: string;
  usc_auto_km_totali_reali: number;
  usc_ora_apertura_latlon: string;
  usc_ora_chiusura_latlon: string;
  usc_note_da_cg: string;
}

export interface AssembledTargetedFetch {
  uscite: UscitaRootCache[];
  uscloc: LocalitaFeature[];
  usclocloc: Volantino[];
  capogruppo: Ragazzo[];
  ragazzi: Ragazzo[];
  carico: Carico[];
  rifornimento: Rifornimento[];
}

const DB_NAME = 'serpa-manifest-full-cache';
// Bumpata da 1 a 2 il 2026-07-09: stesso motivo di manifestCache.ts — possibile DB già a
// versione 2 nel browser da un'iterazione precedente della sessione. Upgrade additivo, bump sicuro.
const DB_VERSION = 2;
const STORES = ['uscita_root', 'uscloc', 'usclocloc', 'uscprod', 'carico', 'rifornimento'] as const;

let dbPromise: Promise<IDBPDatabase> | null = null;

function getDb(): Promise<IDBPDatabase> {
  if (!dbPromise) {
    dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        STORES.forEach((name) => {
          if (!db.objectStoreNames.contains(name)) {
            db.createObjectStore(name, { keyPath: 'recordId' });
          }
        });
      }
    });
  }
  return dbPromise;
}

// Scrive/aggiorna nei rispettivi store il risultato assemblato del fetch mirato. capogruppo/
// ragazzi condividono lo store "uscprod" (stessa tabella FM) con un tag _isCapogruppo aggiunto
// qui in cache — non fa parte della forma Ragazzo restituita dall'API, serve solo a ricostruire
// correttamente i due bucket in buildUsciteFromCache senza un secondo store.
export async function mergeAssembledResults(assembled: AssembledTargetedFetch): Promise<void> {
  const db = await getDb();
  const tx = db.transaction(STORES, 'readwrite');

  await Promise.all([
    ...assembled.uscite.map((u) => tx.objectStore('uscita_root').put(u)),
    ...assembled.uscloc.map((f) => tx.objectStore('uscloc').put(f)),
    ...assembled.usclocloc.map((v) => tx.objectStore('usclocloc').put(v)),
    ...assembled.capogruppo.map((r) => tx.objectStore('uscprod').put({ ...r, _isCapogruppo: true })),
    ...assembled.ragazzi.map((r) => tx.objectStore('uscprod').put({ ...r, _isCapogruppo: false })),
    ...assembled.carico.map((c) => tx.objectStore('carico').put(c)),
    ...assembled.rifornimento.map((r) => tx.objectStore('rifornimento').put(r)),
  ]);
  await tx.done;

  // Un volantino può cambiare senza che la sua zona (uscloc) cambi — in quel caso assembled.uscloc
  // non contiene quella zona (non è stata rifetchata) e il suo Feature in cache resta con
  // properties.volantini non aggiornato. Ricalcola per ogni zona coinvolta, usando lo stato
  // CORRENTE dello store usclocloc (non solo il delta appena scritto sopra).
  if (assembled.usclocloc.length > 0) {
    await refreshVolantiniForAffectedUscloc(
      assembled.usclocloc.map((v) => ({ mob_usc_id: v.mob_usc_id, mob_id_unico: v.mob_usclocloc_id_unico }))
    );
  }
}

async function refreshVolantiniForAffectedUscloc(
  keys: { mob_usc_id: number; mob_id_unico: string }[]
): Promise<void> {
  const db = await getDb();
  const uniqueKeys = Array.from(new Set(keys.map((k) => `${k.mob_usc_id}::${k.mob_id_unico}`)));

  const tx = db.transaction(['uscloc', 'usclocloc'], 'readwrite');
  const allUscloc: LocalitaFeature[] = await tx.objectStore('uscloc').getAll();
  const allVolantini: Volantino[] = await tx.objectStore('usclocloc').getAll();

  for (const key of uniqueKeys) {
    const separatorIndex = key.indexOf('::');
    const uscId = Number(key.slice(0, separatorIndex));
    const idUnico = key.slice(separatorIndex + 2);

    const feature = allUscloc.find(
      (f) => f.properties.mob_usc_id === uscId && f.properties.mob_id_unico === idUnico
    );
    if (!feature) continue; // la zona non è (ancora) in cache — niente da aggiornare

    feature.properties.volantini = allVolantini.filter(
      (v) => v.mob_usc_id === uscId && v.mob_usclocloc_id_unico === idUnico
    );
    await tx.objectStore('uscloc').put(feature);
  }
  await tx.done;
}

// Cancella dalla cache dati pieni tutto ciò che il diff (manifestDiff.ts) ha segnato "removed" —
// da chiamare insieme a manifestCache.ts::syncManifest, stesso ManifestDiffPlan.
export async function deleteRemovedEntities(plan: ManifestDiffPlan): Promise<void> {
  const db = await getDb();

  // Cattura le chiavi (mob_usc_id + id_unico zona) dei volantini rimossi PRIMA di cancellarli —
  // il manifest porta solo recordId+modId, non queste chiavi, servono per ricalcolare
  // properties.volantini della zona che li conteneva.
  const removedVolantiniKeys: { mob_usc_id: number; mob_id_unico: string }[] = [];
  if (plan.usclocloc.removed.length > 0) {
    const readTx = db.transaction('usclocloc', 'readonly');
    for (const entry of plan.usclocloc.removed) {
      const cached: Volantino | undefined = await readTx.store.get(entry.recordId);
      if (cached) {
        removedVolantiniKeys.push({ mob_usc_id: cached.mob_usc_id, mob_id_unico: cached.mob_usclocloc_id_unico });
      }
    }
    await readTx.done;
  }

  const tx = db.transaction(STORES, 'readwrite');
  await Promise.all([
    ...plan.uscite.removed.map((e) => tx.objectStore('uscita_root').delete(e.recordId)),
    ...plan.uscloc.removed.map((e) => tx.objectStore('uscloc').delete(e.recordId)),
    ...plan.usclocloc.removed.map((e) => tx.objectStore('usclocloc').delete(e.recordId)),
    ...plan.uscprod.removed.map((e) => tx.objectStore('uscprod').delete(e.recordId)),
    ...plan.carico.removed.map((e) => tx.objectStore('carico').delete(e.recordId)),
    ...plan.rifornimento.removed.map((e) => tx.objectStore('rifornimento').delete(e.recordId)),
  ]);
  await tx.done;

  if (removedVolantiniKeys.length > 0) {
    await refreshVolantiniForAffectedUscloc(removedVolantiniKeys);
  }
}

// Ricompone Uscita[] (stessa forma di assembleUscitaFromFlatRecords/get-flat) a partire da uno
// snapshot manifest (recordId+modId annidati) leggendo i dati pieni dalla cache. Presuppone che
// il chiamante abbia già fatto, per ogni recordId "added"/"changed" del diff corrispondente, il
// fetch mirato + mergeAssembledResults — altrimenti l'uscita in questione viene saltata (dati
// pieni non ancora disponibili) invece di restituire un oggetto incompleto/scorretto.
export async function buildUsciteFromCache(manifestEntries: ManifestEntry[]): Promise<Uscita[]> {
  const db = await getDb();
  const tx = db.transaction(STORES, 'readonly');

  const uscite: Uscita[] = [];
  for (const entry of manifestEntries) {
    const root: UscitaRootCache | undefined = await tx.objectStore('uscita_root').get(entry.recordId);
    if (!root) continue;

    const tree = entry.manifest;
    const uscIds = tree?.pwa_uscloc.map((u) => u.recordId) || [];
    const uscprodIds = tree?.pwa_uscprod.map((u) => u.recordId) || [];
    const caricoIds = tree?.pwa_carico.map((u) => u.recordId) || [];
    const rifornimentoIds = tree?.pwa_rifornimento.map((u) => u.recordId) || [];

    const localita = (await Promise.all(uscIds.map((id) => tx.objectStore('uscloc').get(id)))).filter(Boolean);
    const uscprodRows: any[] = (
      await Promise.all(uscprodIds.map((id) => tx.objectStore('uscprod').get(id)))
    ).filter(Boolean);
    const carico = (await Promise.all(caricoIds.map((id) => tx.objectStore('carico').get(id)))).filter(Boolean);
    const rifornimento = (
      await Promise.all(rifornimentoIds.map((id) => tx.objectStore('rifornimento').get(id)))
    ).filter(Boolean);

    const stripTag = (r: any) => {
      const { _isCapogruppo, ...rest } = r;
      return rest;
    };
    const capogruppo = uscprodRows.filter((r) => r._isCapogruppo).map(stripTag);
    const ragazzi = uscprodRows.filter((r) => !r._isCapogruppo).map(stripTag);

    uscite.push({
      ...root,
      todo: [] as any[],
      gruppo_cg_nome: capogruppo[0]?.mob_dip_nome || '',
      gruppo_cg_id: capogruppo[0]?.mob_dip_id || 0,
      gruppo_rag_num: ragazzi.length,
      rifornimento,
      carico,
      ragazzi,
      capogruppo,
      localita,
    });
  }
  await tx.done;
  return uscite;
}

// Svuota completamente la cache dati pieni — da usare al logout o insieme a
// manifestCache.ts::clearManifestCache (es. cambio azienda in impersonificazione).
export async function clearFullCache(): Promise<void> {
  const db = await getDb();
  await Promise.all(STORES.map((name) => db.clear(name)));
}

// Popola la cache dati pieni direttamente dalla risposta di get-flat (che porta già tutto:
// nomi reali, recordId, modId — vedi uscitaAssembler.ts). Da chiamare subito dopo ogni
// caricamento completo riuscito, così i cicli di sync incrementale successivi (manifestSync.ts)
// hanno sempre una base completa da cui leggere in buildUsciteFromCache — senza questo passaggio,
// le uscite MAI segnate "changed" da un diff (perché non sono ancora cambiate) risulterebbero
// senza dati pieni in cache e verrebbero saltate.
export async function seedFromUscite(usciteData: Uscita[]): Promise<void> {
  const uscite: UscitaRootCache[] = usciteData.map((u) => ({
    mob_usc_id: u.mob_usc_id,
    recordId: u.recordId,
    mob_soc_id: u.mob_soc_id,
    mob_usc_data: u.mob_usc_data,
    mob_usc_num: u.mob_usc_num,
    mob_mag_id: u.mob_mag_id,
    mob_mag_nome: u.mob_mag_nome,
    mob_mag_latlon: u.mob_mag_latlon,
    mob_usc_auto_targa: u.mob_usc_auto_targa,
    mob_usc_note_x_cg: u.mob_usc_note_x_cg,
    mob_usc_1_fornitore: u.mob_usc_1_fornitore,
    mob_ck_1_show_orari_prev: u.mob_ck_1_show_orari_prev,
    usc_ora_apertura: u.usc_ora_apertura,
    usc_ora_chiusura: u.usc_ora_chiusura,
    usc_ora_inizio_pausa: u.usc_ora_inizio_pausa,
    usc_ora_fine_pausa: u.usc_ora_fine_pausa,
    usc_pausa_tot_ore: u.usc_pausa_tot_ore,
    usc_auto_km_totali_reali: u.usc_auto_km_totali_reali,
    usc_ora_apertura_latlon: u.usc_ora_apertura_latlon,
    usc_ora_chiusura_latlon: u.usc_ora_chiusura_latlon,
    usc_note_da_cg: u.usc_note_da_cg,
  }));

  const uscloc: LocalitaFeature[] = usciteData.flatMap((u) => u.localita || []);
  const usclocloc: Volantino[] = uscloc.flatMap((f) => f.properties.volantini || []);
  const capogruppo: Ragazzo[] = usciteData.flatMap((u) => u.capogruppo || []);
  const ragazzi: Ragazzo[] = usciteData.flatMap((u) => u.ragazzi || []);
  const carico: Carico[] = usciteData.flatMap((u) => u.carico || []);
  const rifornimento: Rifornimento[] = usciteData.flatMap((u) => u.rifornimento || []);

  await mergeAssembledResults({ uscite, uscloc, usclocloc, capogruppo, ragazzi, carico, rifornimento });
}
